import * as Sentry from '@sentry/nextjs'
import Router from 'next/router'

import type { CheckoutPath } from '@/lib/checkout/types'
import { PURCHASE_TRANSACTION_NAME } from '@/lib/sentry/performanceTracing'

/**
 * Sentry timing for the two paid journey steps around Stripe (see
 * `docs/data-flows/paid-journey-steps.md`): step 9, pay on Stripe, and step 10, purchase recorded,
 * on the page Stripe returns the customer to.
 *
 * Both are transactions of their own rather than children of the page's navigation, which has
 * long finished by the time someone presses Pay.
 */

export type PaymentOutcome =
  // Stripe accepted the payment and is sending the customer to the return URL.
  | 'redirected'
  // `onBeforeConfirm` blocked payment (eligibility, existing account, credential linking).
  | 'blocked'
  // Stripe rejected the payment; the error code is on the `payment_error` attribute.
  | 'payment_error'

export interface PaymentTiming {
  /** Call once `onBeforeConfirm` has resolved, just before `checkout.confirm()`. */
  confirmStarted: () => void
  end: (outcome: PaymentOutcome, detail?: string) => void
}

const NOOP_TIMING: PaymentTiming = { confirmStarted: () => {}, end: () => {} }

/**
 * Starts the pay transaction on the Pay click, named after the checkout route so the sampler and
 * dashboards group it with the page. Child spans split our pre-confirm work from Stripe's confirm.
 * A successful confirm navigates away without resolving, so the page being hidden ends it as
 * `redirected`.
 */
export const startPaymentTiming = (checkoutPath: CheckoutPath): PaymentTiming => {
  // Timing must never break the Pay button: on any Sentry failure, pay without it.
  try {
    if (typeof window === 'undefined') return NOOP_TIMING
    const route = Router.router?.pathname
    if (!route) return NOOP_TIMING

    const startedAt = performance.now()
    const transaction = Sentry.startInactiveSpan({ name: route, op: 'checkout.pay', forceTransaction: true })
    if (!transaction) return NOOP_TIMING
    transaction.setAttribute('checkout_path', checkoutPath)

    const startChild = (name: string, op: string) =>
      Sentry.withActiveSpan(transaction, () => Sentry.startInactiveSpan({ name, op }))

    let currentStep = startChild('Pre-confirm checks', 'checkout.pay.pre_confirm')
    let ended = false

    const end = (outcome: PaymentOutcome, detail?: string) => {
      if (ended) return
      ended = true
      try {
        window.removeEventListener('pagehide', onPageHide)
        currentStep?.end()
        transaction.setAttribute('payment_outcome', outcome)
        if (detail) transaction.setAttribute('payment_error', detail)
        transaction.setStatus(outcome === 'redirected' ? 'ok' : 'failed_precondition')
        // Journey step 9, pay on Stripe: Pay click until Stripe resolves or takes the customer away.
        Sentry.withActiveSpan(transaction, () =>
          Sentry.setMeasurement(
            'journey.step9_pay_on_stripe',
            Math.round(performance.now() - startedAt),
            'millisecond',
          ),
        )
        transaction.end()
      } catch {
        // Dropped: a lost timing is better than a payment flow that throws.
      }
    }

    const onPageHide = () => end('redirected')
    window.addEventListener('pagehide', onPageHide)

    return {
      confirmStarted: () => {
        try {
          currentStep?.end()
          currentStep = startChild('Stripe confirm', 'checkout.pay.confirm')
        } catch {
          // Dropped, as in `end`.
        }
      },
      end,
    }
  } catch {
    return NOOP_TIMING
  }
}

export type PurchaseOutcome = 'complete' | 'open'

// The product a basket is bought for, most specific first: a cross-sell return page serves every
// product, so this is what ties step 10 to one.
const PURCHASE_PRODUCTS = ['ZOE_PLUS', 'DAILY30', 'BARS', 'GUT_MICROBIOME_TEST']

/** The main product among a session's line item product types, or `other`. */
export const purchaseProductOf = (productTypes: readonly (string | undefined)[]): string =>
  PURCHASE_PRODUCTS.find((product) => productTypes.includes(product)) ?? 'other'

// `performance.now()` at the start of the latest client-side navigation. A cross-sell offer moves on
// with `router.push`, so the document's own start would include the time spent on the offer.
let navigationStartedAt = 0
if (typeof window !== 'undefined') {
  try {
    Router.events.on('routeChangeStart', () => {
      navigationStartedAt = performance.now()
    })
  } catch {
    // Without it, step 10 starts at the document's own start.
  }
}

// Session and outcome pairs already sent, so a re-run of the caller's effect cannot send one twice.
const recordedPurchases = new Set<string>()

/**
 * Marks a checkout session read on the return page: from the start of the page (or of the
 * client-side navigation that reached it) until its status was known. Sent once per session and
 * outcome in this document, so an OPEN session that later completes is sent as both. `product` is
 * the session's main product, from `purchaseProductOf`.
 */
export const recordPurchase = (
  sessionId: string,
  outcome: PurchaseOutcome,
  product: string,
  checkoutPath: CheckoutPath,
): void => {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return
  const key = `${sessionId}:${outcome}`
  if (recordedPurchases.has(key)) return
  recordedPurchases.add(key)

  // Timing must never break purchase tracking: on any Sentry failure, the step is not sent.
  try {
    const startedAt = navigationStartedAt
    const transaction = Sentry.startInactiveSpan({
      name: PURCHASE_TRANSACTION_NAME,
      op: 'checkout.purchase',
      forceTransaction: true,
      startTime: (performance.timeOrigin + startedAt) / 1000,
    })
    if (!transaction) return
    transaction.setAttribute('purchase_outcome', outcome)
    transaction.setAttribute('purchase_product', product)
    transaction.setAttribute('checkout_path', checkoutPath)
    const route = Router.router?.pathname
    if (route) transaction.setAttribute('return_route', route)
    // Journey step 10, purchase recorded: return page start until the purchase is known.
    Sentry.withActiveSpan(transaction, () =>
      Sentry.setMeasurement(
        'journey.step10_purchase_recorded',
        Math.round(performance.now() - startedAt),
        'millisecond',
      ),
    )
    transaction.end()
  } catch {
    // Dropped, as in `startPaymentTiming`.
  }
}
